import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import ErrorScreen from "../components/ErrorScreen";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import { formatTime } from "../utils/time";
import { formatIndianMoney } from "../utils/money";
import { hasModuleRestriction, isBuyFullCourseQuery, normalizeModuleIds } from "../utils/purchase";
import { USER_BASE_URL, VERSION } from "../utils/config";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "../styles/Course.css";
import "../styles/Loading.css";
import "../styles/FaqTestimonial.css";
import "../styles/ModuleLesson.css";
import ProfileComplete from "./ProfileComplete";
import PaymentSheet from "../components/PaymentSheet";
import FAQItem from "../components/FAQItem";
import ModuleItem from "../components/ModuleItem";
import Navbar from "../components/Navbar";
import CourseDescription from "../components/CourseDescription";

export default function Course() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const buyFullCourse = isBuyFullCourseQuery(searchParams.get("buy_full_course"));
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showPayment, setShowPayment] = useState(false);
  const [courseAlreadyPurchased, setCourseAlreadyPurchased] = useState(false);
  const [course, setCourse] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [error, setError] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const swiperRef = useRef(null);
  const autoOpenedRef = useRef(false);

  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [modules, setModules] = useState([]);

  const navigate = useNavigate();

  const tabs = ["overview", "lessons"];
  const unlockedModuleIds = normalizeModuleIds(purchaseData?.unlocked_module_ids);
  const hasFullAccess =
    purchaseData?.payment_status === "success" &&
    !purchaseData?.course_expired &&
    !hasModuleRestriction(purchaseData);

  const applyPurchaseState = useCallback((purchase) => {
    setPurchaseData(purchase || null);
    if (purchase && !purchase.course_expired && purchase.payment_status === "success") {
      const restricted = hasModuleRestriction(purchase);
      setCourseAlreadyPurchased(!restricted || !buyFullCourse);
      return;
    }
    setCourseAlreadyPurchased(false);
  }, [buyFullCourse]);

  useEffect(() => {
    if (!id) navigate(-1);
  }, [id, navigate]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    const index = tabs.indexOf(tab);
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/course/web/overview`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );

        if (data) {
          setCourse(data.course_data);
          applyPurchaseState(data.purchase_data);
        } else {
          showToast("Error Fetching Data!", "error");
          navigate("/dashboard");
        }

        const faqRes = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/faq/web/course/list`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );
        if (faqRes){
          setFaqs(faqRes);
        }

        const testiRes = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/testimonial/web/course/list`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );
        if (testiRes) {
          setTestimonials(testiRes.sort((a, b) => a.index - b.index));
        }

        const modRes = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/course/web/module-list`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );
        if (modRes && modRes.modules) {
          setModules(modRes.modules.sort((a, b) => a.index - b.index));
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        if (err.type === "version_reloading") return;
        if (err.code === 406) setError(err.type || "account_blocked");
        else if (err.code === 400) {
          setError("400");
          setErrorMsg(err.message);
        } else setError("server_error");
        setLoading(false);
      }
    };
    fetchData();
  }, [USER_BASE_URL, VERSION, id, navigate, applyPurchaseState]);

  useEffect(() => {
    if (!id) return undefined;

    const refreshPurchase = async () => {
      try {
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/course/web/overview`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );
        if (data) {
          applyPurchaseState(data.purchase_data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshPurchase();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [USER_BASE_URL, VERSION, id, applyPurchaseState]);

  useEffect(() => {
    if (buyFullCourse && course && !courseAlreadyPurchased && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setShowPayment(true);
    }
  }, [buyFullCourse, course, courseAlreadyPurchased]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading</p>
        </div>
      </div>
    );
  }

    if (errorMsg === "Please complete your profile first.") {
      return <ProfileComplete />;
    }

  if (error) {
    return (
      <ErrorScreen
        type={error}
        {...(error === "400" && {
          onMessage: errorMsg,
          onAction: () => window.open("https://share.g9eduinternational.com/course", "_blank"),
        })}
      />
    );
  }

  if (courseAlreadyPurchased) {
    return (
      <div className="course-details-wrapper">
        <Navbar fixed />
        <header className="course-header">
          <button className="back-btn-new" onClick={() => navigate("/dashboard")}>
            <img src="/assets/images/arrow_back_ios.png" alt="Back" />
          </button>
          <h1 className="header-title">{course.name}</h1>
        </header>
        <ErrorScreen
          type="course_already_purchased"
          onAction={() => window.open(`https://share.g9eduinternational.com/course/${id}`, "_blank")}
          navRequired={false}
        />
      </div>
    );
  }

  return (
    <div className="course-details-wrapper">
      <Navbar fixed />
      {course ? (
        <>
          {/* Header */}
          <header className="course-header">
            <button className="back-btn-new" onClick={() => navigate("/dashboard")}>
              <img src="/assets/images/arrow_back_ios.png" alt="Back" />
            </button>
            <h1 className="header-title">{course.name}</h1>
          </header>

          {/* Banner */}
          <div className="course-banner">
            <img src={course.banner_image.url} alt={course.name} />
          </div>

          <div className="tabs">
            {tabs.map((b) => (
              <button
                key={b}
                className={`tab ${activeTab === b ? "active" : ""}`}
                onClick={() => handleTabClick(b)}
              >
                {b === "overview" ? "Overview" : "Lessons"}
              </button>
            ))}
          </div>

          <Swiper
            slidesPerView={1}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => {
              setActiveTab(tabs[swiper.activeIndex]);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            initialSlide={activeTab === "overview" ? 0 : 1}
            noSwipingSelector=".testimonial-slider"
          >
            <SwiperSlide key={"overview"}>
              <div className="course-content">
                <h2 className="course-title">{course.name}</h2>
                <CourseDescription description={course.description} />

                <div className="instructor">
                  <img
                    className="instructor-photo"
                    src={
                      course.instructor_id.profile_image
                        ? course.instructor_id.profile_image.url
                        : "/assets/images/profile.jpg"
                    }
                    alt={course.instructor_id.name}
                  />
                  <div>
                    <h4 className="instructor-name">{course.instructor_id.name}</h4>
                    <p className="instructor-qual">{course.instructor_id.educational_qualification}</p>
                  </div>
                </div>

                <h3 className="includes-title">What's included?</h3>
                <div className="includes-grid">
                  <div className="include-item bg-blue">
                    <span className="include-icon"><img src="/assets/images/video_n.png" alt="Lessons" /></span>
                    <p>{course.lesson_count} Lessons</p>
                  </div>
                  <div className="include-item bg-red">
                    <span className="include-icon"><img src="/assets/images/pdf_n.png" alt="PDF Notes" /></span>
                    <p>PDF Notes</p>
                  </div>
                  <div className="include-item bg-green">
                    <span className="include-icon"><img src="/assets/images/camera_n.png" alt="Q&A" /></span>
                    <p>Live Q&A Session</p>
                  </div>
                  <div className="include-item bg-orange">
                    <span className="include-icon"><img src="/assets/images/test_n.png" alt="Tests" /></span>
                    <p>Practice Tests</p>
                  </div>
                  <div className="include-item bg-violet">
                    <span className="include-icon"><img src="/assets/images/clock_n.png" alt="Duration" /></span>
                    <p>{formatTime(course.video_duration + course.test_duration)}</p>
                  </div>
                  <div className="include-item bg-light-blue">
                    <span className="include-icon"><img src="/assets/images/certs_n.png" alt="Certificates" /></span>
                    <p>Certificates</p>
                  </div>
                </div>

                <section className="faq-section">
                  <h3 className="section-title">FAQ’s</h3>
                  {faqs.length === 0 ? (
                    <p className="no-data">No FAQs available</p>
                  ) : (
                    faqs.map((f, i) => (
                      <FAQItem key={f._id} faq={f} />
                    ))
                  )}
                </section>

                <section className="testimonial-section">
                  <h3 className="section-title">What our learners say</h3>
                  {testimonials.length === 0 ? (
                    <p className="no-data">No testimonials available</p>
                  ) : (
                    <div className="testimonial-slider">
                      {testimonials.map((t) => (
                        <div key={t._id} className="testimonial-card">
                          <p className="testimonial-text">“{t.description}”</p>
                          <div className="testimonial-user">
                            <img
                              src={
                                ((t.student_id && t.student_id.gender) ? t.student_id.gender : (t.gender || "male")) === "female"
                                  ? "/assets/images/female_avatar.svg"
                                  : "/assets/images/male_avatar.svg"
                              }
                              alt={(t.student_id && t.student_id.name) ? t.student_id.name : (t.name || "User")}
                            />
                            <div>
                              <h4>{(t.student_id && t.student_id.name) ? t.student_id.name : (t.name || "User")}</h4>
                              <p>{t.certification}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </SwiperSlide>

            <SwiperSlide key={"lessons"}>
              <div className="course-content">
                <section className="module-section">
                  {modules.length === 0 ? (
                    <p className="no-data">No Modules Available</p>
                  ) : (
                    modules.map((f, i) => (
                      <ModuleItem
                        key={f._id}
                        module={f}
                        index={i}
                        hasFullAccess={hasFullAccess}
                        unlockedModuleIds={unlockedModuleIds}
                      />
                    ))
                  )}
                </section>
              </div>
            </SwiperSlide>

          </Swiper>

          <div className="bottom-bar">
            <div className="price-section">
              <span className="old-price">
                ₹{formatIndianMoney(course.mrp)}{" "}
                <small>({Math.ceil(((course.mrp - course.price) / course.mrp) * 100)}% off)</small>
              </span>
              <span className="new-price">₹{formatIndianMoney(course.price)} Only</span>
            </div>
            <button className="enroll-btn" onClick={() => setShowPayment(true)}>
              {buyFullCourse ? "Buy Full Course" : "Enroll Now"}
            </button>
          </div>

          <PaymentSheet
            open={showPayment}
            courseDetails={course}
            buyFullCourse={buyFullCourse}
            onClose={() => setShowPayment(false)}
          />
        </>
      ) : (
        <p className="not-found">Course not found</p>
      )}
    </div>
  );
}
